/* JC-EVO 09/26/2026: blog list "Show more" for sections/jc-blog-list.liquid. Progressive enhancement only: without this
   file "Show more" is a plain link to the next page (?page=N), which is also what crawlers follow.
   A plain click fetches the next page's section (Section Rendering API), adds its posts to the list (skipping any post
   already shown), points the link at the following page (or removes it after the last page), moves focus to the first
   new post and announces it once. The URL stays the same; the pages added are kept in history.state, so Back (or a
   reload) rebuilds the same list and re-applies the saved scroll position. Any failure opens the next page instead. */
(() => {
  const root = document.querySelector('[data-jc-bl]');
  if (!root || root.dataset.jcBlReady) return;
  const list = root.querySelector('[data-jc-bl-list]');
  const status = root.querySelector('[data-jc-bl-status]');
  const sectionId = root.dataset.sectionId;
  if (!list || !sectionId) return;
  root.dataset.jcBlReady = '1';
  const here = location.pathname + location.search;
  const ids = new Set([...list.children].map((li) => li.dataset.jcBlId));
  let busy = false;

  // page URL without Section Rendering API parameters (the link must keep working as a normal page link)
  const clean = (href) => {
    const u = new URL(href, location.href);
    u.searchParams.delete('section_id');
    u.searchParams.delete('sections');
    return u.pathname + u.search;
  };

  const fetchPage = async (href) => {
    const u = new URL(href, location.href);
    u.searchParams.set('section_id', sectionId);
    // 10 s cap: a hung request must not leave the link dead (click falls back to the page; Back shows page 1)
    const signal = typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? AbortSignal.timeout(10000) : undefined;
    const res = await fetch(u, { credentials: 'same-origin', signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const newList = doc.querySelector('[data-jc-bl-list]');
    if (!newList) throw new Error('no list in response');
    const next = doc.querySelector('[data-jc-bl-more]');
    return { items: [...newList.children], next: next ? clean(next.getAttribute('href')) : null };
  };

  const add = (items) => {
    const added = [];
    for (const li of items) {
      const id = li.dataset.jcBlId;
      if (!id || ids.has(id)) continue; // a post published since this page loaded shifts the pages by one
      ids.add(id);
      const node = document.importNode(li, true);
      list.append(node);
      added.push(node);
    }
    return added;
  };

  const setNext = (next) => {
    const more = root.querySelector('[data-jc-bl-more]');
    if (!more) return;
    if (next) {
      more.setAttribute('href', next);
      return;
    }
    const pager = more.parentElement;
    more.remove();
    if (pager && !pager.querySelector('a')) pager.remove();
  };

  const saveState = (patch) => {
    try {
      const s = history.state && typeof history.state === 'object' ? history.state : {};
      const prev = s.jcBl && s.jcBl.list === here ? s.jcBl : { list: here, urls: [] };
      history.replaceState({ ...s, jcBl: { ...prev, ...patch } }, '');
    } catch (e) {
      /* state too large or blocked: the list simply is not rebuilt on Back */
    }
  };

  // the page scroller: Horizon scrolls .page-wrapper on desktop, the document elsewhere
  const scroller = () => {
    const pw = document.querySelector('.page-wrapper');
    if (pw && /(auto|scroll)/.test(getComputedStyle(pw).overflowY) && pw.scrollHeight > pw.clientHeight) return pw;
    return document.scrollingElement || document.documentElement;
  };

  const say = (text) => {
    if (!status) return;
    status.textContent = '';
    setTimeout(() => {
      status.textContent = text;
    }, 100);
  };

  root.addEventListener('click', async (event) => {
    const more = event.target.closest('[data-jc-bl-more]');
    if (!more || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (busy) return;
    busy = true;
    const url = clean(more.getAttribute('href'));
    list.setAttribute('aria-busy', 'true');
    try {
      const { items, next } = await fetchPage(url);
      const added = add(items);
      if (!added.length && next) throw new Error('no new posts');
      setNext(next);
      const s = history.state && history.state.jcBl;
      saveState({ urls: [...(s && s.list === here && Array.isArray(s.urls) ? s.urls : []), url] });
      const target = (added[0] || list.lastElementChild)?.querySelector('.jc-bl__link');
      if (target) target.focus();
      if (added.length) say(`Loaded ${added.length} more post${added.length === 1 ? '' : 's'}.`);
    } catch (e) {
      location.assign(url); // the real next page
    } finally {
      busy = false;
      list.removeAttribute('aria-busy');
    }
  });

  // leaving the page: remember the scroll position with the added pages (Horizon keeps its own copy too)
  window.addEventListener('pagehide', () => {
    const s = history.state;
    if (s && s.jcBl && s.jcBl.list === here && Array.isArray(s.jcBl.urls) && s.jcBl.urls.length) saveState({ y: scroller().scrollTop });
  });

  // Back / reload of this list (a fresh load of this history entry; a bfcache restore keeps the page and never re-runs
  // this file): rebuild the pages added before, then restore the scroll position. The list stays invisible meanwhile
  // (at most 5 s), so the reader does not see it jump from Horizon's early, too-short restore to the right place.
  const saved = history.state && history.state.jcBl;
  if (saved && saved.list === here && Array.isArray(saved.urls) && saved.urls.length) {
    (async () => {
      busy = true;
      list.setAttribute('aria-busy', 'true');
      const y = Number.isFinite(saved.y) ? saved.y : history.state && history.state.scrollTop;
      // Horizon (scroll-container.js) would restore history.state.scrollTop on pageshow against the short page first;
      // take it out of the state (this file runs before pageshow) so only the restore below moves the page
      try {
        const { scrollTop, ...rest } = history.state;
        if (scrollTop != null) history.replaceState(rest, '');
      } catch (e) {
        /* keep Horizon's copy */
      }
      root.setAttribute('data-jc-bl-restoring', '');
      let shown = false; // after the 5 s cap the reader sees the list: late pages are added below, the page is not moved
      const cap = setTimeout(() => {
        shown = true;
        root.removeAttribute('data-jc-bl-restoring');
      }, 5000);
      const urls = saved.urls.slice(0, 20);
      const pages = await Promise.all(urls.map((url) => fetchPage(url).catch(() => null)));
      const done = [];
      for (const [i, page] of pages.entries()) {
        if (!page) break; // keep the pages in order: stop at the first one that failed
        add(page.items);
        setNext(page.next);
        done.push(urls[i]);
      }
      busy = false;
      list.removeAttribute('aria-busy');
      saveState({ urls: done });
      if (!shown && Number.isFinite(y) && y > 0) {
        const sc = scroller();
        sc.scrollTo({ top: Math.min(y, sc.scrollHeight - sc.clientHeight), behavior: 'instant' });
      }
      clearTimeout(cap);
      root.removeAttribute('data-jc-bl-restoring');
    })();
  }
})();
